import type { CellContext } from '@tanstack/react-table'
import type { BadgeVariant, ColumnMeta, DataType } from '../types'
import { getNestedValue } from '../types'
import { CellText } from './cell-text'
import { CellNumber } from './cell-number'
import { CellDate } from './cell-date'
import { CellBadge } from './cell-badge'
import { CellBoolean } from './cell-boolean'
import { CellLink } from './cell-link'
import { CellExternalLink } from './cell-external-link'
import { EditableCell } from './editable-cell'
import { InputCell } from './input-cell'
import { OwnerCell } from './owner-cell'
import { LocationFieldCell } from './location-field-cell'
import type { RelationshipItem } from './relationship-editor'
import type { UpdateCompany } from '@/types/api'
import { getFormatter } from '@/lib/format'
import { formatLocationDisplay } from '@/lib/formatters'

export { CellText } from './cell-text'
export { CellNumber } from './cell-number'
export { CellDate } from './cell-date'
export { CellBadge } from './cell-badge'
export { CellBoolean } from './cell-boolean'
export { CellLink } from './cell-link'
export { CellExternalLink } from './cell-external-link'
export { EditableCell } from './editable-cell'
export { InputCell } from './input-cell'
export { OwnerCell } from './owner-cell'
export { LocationFieldCell } from './location-field-cell'

// Location fields that can be edited independently
const LOCATION_FIELDS = [
  'location.country.name',
  'location.state.name',
] as const
type LocationFieldType = (typeof LOCATION_FIELDS)[number]

function isLocationField(fieldName: string): fieldName is LocationFieldType {
  return LOCATION_FIELDS.includes(fieldName as LocationFieldType)
}

// Editable relationship fields
const EDITABLE_FIELDS = ['industries', 'sectors', 'location'] as const
type EditableFieldType = (typeof EDITABLE_FIELDS)[number]

function isEditableField(fieldName: string): fieldName is EditableFieldType {
  return EDITABLE_FIELDS.includes(fieldName as EditableFieldType)
}

// Owner fields that use searchable user dropdown
const OWNER_FIELDS = ['primaryOwner', 'secondaryOwner'] as const
type OwnerFieldType = (typeof OWNER_FIELDS)[number]

function isOwnerField(fieldName: string): fieldName is OwnerFieldType {
  return OWNER_FIELDS.includes(fieldName as OwnerFieldType)
}

// Data types that can use InputCell for editing
const INPUT_EDITABLE_TYPES = ['string', 'number'] as const
type InputEditableType = (typeof INPUT_EDITABLE_TYPES)[number]

function isInputEditableType(
  dataType: DataType
): dataType is InputEditableType {
  return INPUT_EDITABLE_TYPES.includes(dataType as InputEditableType)
}

// Valid fields for UpdateCompany API
const UPDATE_COMPANY_FIELDS: (keyof UpdateCompany)[] = [
  'displayName',
  'legalName',
  'description',
  'website',
  'geographicFocus',
  'fiscalYearEnd',
  'logoUrl',
  'ownershipPercentage',
  'irr',
  'boardSeats',
  'greybullBoardSeats',
  'employeeCount',
]

function isValidUpdateField(
  fieldName: string
): fieldName is keyof UpdateCompany {
  return UPDATE_COMPANY_FIELDS.includes(fieldName as keyof UpdateCompany)
}

/**
 * Extract display value from an item that could be a string or an object
 * @param item - The item to extract value from
 * @param displayProperty - Optional specific property to extract (e.g., 'abbreviation')
 */
function getDisplayValue(item: unknown, displayProperty?: string): string {
  if (item === null || item === undefined) return ''
  if (typeof item === 'string') return item
  if (typeof item === 'object') {
    const obj = item as Record<string, unknown>
    // If specific property requested, try that first
    if (displayProperty && typeof obj[displayProperty] === 'string') {
      return obj[displayProperty] as string
    }
    // Fallback to common display properties
    if (typeof obj.displayName === 'string') return obj.displayName
    if (typeof obj.name === 'string') return obj.name
    if (typeof obj.label === 'string') return obj.label
    if (typeof obj.abbreviation === 'string') return obj.abbreviation
  }
  return String(item)
}

/**
 * Generic cell renderer that selects the appropriate cell component
 * based on the column's data type
 */
export function renderCell<TData extends Record<string, unknown>>(
  info: CellContext<TData, unknown>
) {
  const value = info.getValue()
  const meta = info.column.columnDef.meta as ColumnMeta | undefined
  const fieldName = meta?.fieldName ?? ''
  const dataType = meta?.dataType ?? 'string'
  const options = meta?.options
  const badgeVariant = meta?.badgeVariant
  const displayProperty = meta?.displayProperty
  const format = meta?.format
  const tableMeta = info.table.options.meta as {
    searchTerm?: string
    onRowClick?: (row: TData) => void
  }
  const searchTerm = tableMeta?.searchTerm

  const isEditable = meta?.isEditable ?? false

  // Render displayName as a clickable link that opens the side peek
  if (fieldName === 'displayName') {
    const row = info.row.original
    return (
      <CellLink
        value={value as string | null | undefined}
        onClick={() => tableMeta?.onRowClick?.(row)}
        searchTerm={searchTerm}
      />
    )
  }

  // Render website as a clickable external link
  if (fieldName === 'website') {
    return (
      <CellExternalLink
        value={value as string | null | undefined}
        searchTerm={searchTerm}
      />
    )
  }

  // Extract asOfDate for financial metrics
  let asOfDate: string | Date | null = null
  if (fieldName.startsWith('financials.')) {
    const row = info.row.original
    asOfDate =
      (getNestedValue(row, 'financials.ltmPeriodEndDate') as string) ?? null
  }

  // Check if this is an editable location field (country or state)
  if (isEditable && isLocationField(fieldName)) {
    const row = info.row.original
    const companyId = row.id as string | undefined
    const primaryOwnerId = getNestedValue(row, 'primaryOwner.id') as
      | string
      | null
    const secondaryOwnerId = getNestedValue(row, 'secondaryOwner.id') as
      | string
      | null

    if (companyId) {
      // Get the location data from the row
      const location = getNestedValue(row, 'location') as {
        country?: { id: string; name: string } | null
        state?: { id: string; name: string } | null
        city?: { id: string; name: string } | null
      } | null

      return (
        <LocationFieldCell
          companyId={companyId}
          primaryOwnerId={primaryOwnerId}
          secondaryOwnerId={secondaryOwnerId}
          fieldType={fieldName}
          currentCountry={location?.country}
          currentState={location?.state}
          currentCity={location?.city}
        />
      )
    }
  }

  // Check if this is an editable relationship field
  if (isEditableField(fieldName)) {
    const row = info.row.original
    const companyId = row.id as string | undefined
    const primaryOwnerId = getNestedValue(row, 'primaryOwner.id') as
      | string
      | null
    const secondaryOwnerId = getNestedValue(row, 'secondaryOwner.id') as
      | string
      | null

    if (companyId) {
      // Transform value to RelationshipItem array
      let currentValues: RelationshipItem[] = []

      if (fieldName === 'location') {
        // Location is a single object, wrap in array if present
        if (value && typeof value === 'object') {
          const loc = value as {
            id?: string
            city?: { name: string } | null
            state?: { name: string } | null
            country?: { name: string } | null
          }
          // Location doesn't have an ID directly, use a composite or skip
          // For now, we'll use the display name as identifier
          const displayName = formatLocationDisplay(loc)
          if (displayName) {
            currentValues = [{ id: loc.id ?? displayName, name: displayName }]
          }
        }
      } else {
        // Industries and sectors are arrays
        if (Array.isArray(value)) {
          currentValues = value.map((v) => ({
            id: (v as { id: string }).id,
            name: (v as { name: string }).name,
          }))
        }
      }

      return (
        <EditableCell
          companyId={companyId}
          primaryOwnerId={primaryOwnerId}
          secondaryOwnerId={secondaryOwnerId}
          fieldType={fieldName}
          currentValues={currentValues}
          displayProperty={displayProperty}
        />
      )
    }
  }

  // Check if this is an owner field (single-select user dropdown)
  if (isOwnerField(fieldName)) {
    const row = info.row.original
    const companyId = row.id as string | undefined
    const primaryOwnerId = getNestedValue(row, 'primaryOwner.id') as
      | string
      | null
    const secondaryOwnerId = getNestedValue(row, 'secondaryOwner.id') as
      | string
      | null

    if (companyId) {
      const ownerValue = value as
        | { id: string; name: string; email?: string }
        | null
        | undefined

      return (
        <OwnerCell
          companyId={companyId}
          primaryOwnerId={primaryOwnerId}
          secondaryOwnerId={secondaryOwnerId}
          fieldType={fieldName}
          currentOwner={ownerValue ?? null}
        />
      )
    }
  }

  // Check if this is an editable input field (string or number)
  if (isEditable && isInputEditableType(dataType)) {
    const row = info.row.original
    const companyId = row.id as string | undefined
    const primaryOwnerId = getNestedValue(row, 'primaryOwner.id') as
      | string
      | null
    const secondaryOwnerId = getNestedValue(row, 'secondaryOwner.id') as
      | string
      | null

    if (companyId && isValidUpdateField(fieldName)) {
      return (
        <InputCell
          companyId={companyId}
          primaryOwnerId={primaryOwnerId}
          secondaryOwnerId={secondaryOwnerId}
          fieldName={fieldName}
          value={value as string | number | null | undefined}
          inputType={dataType === 'number' ? 'number' : 'text'}
          formatDisplay={getFormatter(format)}
        />
      )
    }
  }

  return (
    <CellRenderer
      value={value}
      dataType={dataType}
      options={options}
      badgeVariant={badgeVariant}
      displayProperty={displayProperty}
      format={format}
      searchTerm={searchTerm}
      asOfDate={asOfDate}
    />
  )
}

interface CellRendererProps {
  value: unknown
  dataType: DataType
  options?: string[]
  badgeVariant?: BadgeVariant
  displayProperty?: string
  format?: string
  searchTerm?: string
  asOfDate?: string | Date | null
}

function isNumericValue(
  value: unknown
): value is number | string | null | undefined {
  return typeof value === 'number' || typeof value === 'string' || value == null
}

function CellRenderer({
  value,
  dataType,
  options,
  badgeVariant,
  displayProperty,
  format,
  searchTerm,
  asOfDate,
}: CellRendererProps) {
  switch (dataType) {
    case 'number':
      if (!isNumericValue(value)) {
        return <span className="text-muted-foreground">-</span>
      }
      return <CellNumber value={value} format={format} asOfDate={asOfDate} />

    case 'boolean':
      return <CellBoolean value={value as boolean | string | null} />

    case 'date':
      return <CellDate value={value as string | Date | null} format="date" />

    case 'datetime':
      return (
        <CellDate value={value as string | Date | null} format="datetime" />
      )

    case 'enum':
      return (
        <CellBadge
          value={value as string | null}
          options={options}
          variant={badgeVariant}
        />
      )

    case 'multiselect':
      if (Array.isArray(value)) {
        const sortedValues = [...value].sort((a, b) => {
          const aVal = getDisplayValue(a, displayProperty)
          const bVal = getDisplayValue(b, displayProperty)
          return String(aVal ?? '').localeCompare(String(bVal ?? ''))
        })
        return (
          <div className="flex flex-wrap gap-2.5">
            {sortedValues.map((v, i) => (
              <CellBadge
                key={i}
                value={getDisplayValue(v, displayProperty)}
                options={options}
                variant={badgeVariant}
              />
            ))}
          </div>
        )
      }
      return <CellText value={value as string | null} searchTerm={searchTerm} />

    case 'array':
      if (Array.isArray(value)) {
        const sortedValues = [...value].sort((a, b) => {
          const aVal = getDisplayValue(a, displayProperty)
          const bVal = getDisplayValue(b, displayProperty)
          return String(aVal ?? '').localeCompare(String(bVal ?? ''))
        })
        // If badgeVariant is set, render as badges instead of text
        if (badgeVariant) {
          return (
            <div className="flex flex-wrap gap-2.5">
              {sortedValues.map((v, i) => (
                <CellBadge
                  key={i}
                  value={getDisplayValue(v, displayProperty)}
                  options={options}
                  variant={badgeVariant}
                />
              ))}
            </div>
          )
        }
        return (
          <CellText
            value={sortedValues
              .map((v) => getDisplayValue(v, displayProperty))
              .join(', ')}
            searchTerm={searchTerm}
          />
        )
      }
      return <CellText value={value as string | null} searchTerm={searchTerm} />

    case 'object':
      // If badgeVariant is set, render as badge
      if (badgeVariant) {
        return (
          <CellBadge
            value={getDisplayValue(value, displayProperty)}
            options={options}
            variant={badgeVariant}
          />
        )
      }
      return (
        <CellText
          value={getDisplayValue(value, displayProperty)}
          searchTerm={searchTerm}
        />
      )

    case 'uuid':
    case 'string':
    default:
      return <CellText value={value as string | null} searchTerm={searchTerm} />
  }
}
